// Диалог правки валюты (extract из CurrenciesTab, SLED-EXAM-FE-V17-W5).

import { useEffect } from 'react';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';
import type { AdminCurrency } from '@/api/adminDictionaries';
import { t } from '@/i18n';

/** Zod-схема правки валюты: символ и активность; код в пути, не в теле. */
const currencyEditSchema = z.object({
  symbol: z.string().trim().min(1, 'Символ обязателен').max(8, 'Слишком длинный символ'),
  active: z.boolean(),
});

/** Значения формы правки валюты. */
type CurrencyEditValues = z.infer<typeof currencyEditSchema>;

/** Пропсы диалога правки валюты. */
export interface CurrencyEditDialogProps {
  /** Открыт ли диалог. */
  open: boolean;
  /** Редактируемая валюта; код показывается disabled. */
  currency: AdminCurrency | null;
  /** Закрыть без сохранения. */
  onClose: () => void;
  /** Валидный submit: только symbol+active (PUT код не меняется). */
  onSubmit: (values: { symbol: string; active: boolean }) => void;
  /** Блокировка кнопки «Сохранить» на время мутации. */
  isPending?: boolean;
}

/**
 * Диалог правки валюты.
 * Контракт: код только для чтения; submit — `{ symbol, active }` без `code`.
 */
export function CurrencyEditDialog({
  open,
  currency,
  onClose,
  onSubmit,
  isPending = false,
}: CurrencyEditDialogProps): JSX.Element {
  const form = useForm<CurrencyEditValues>({
    resolver: zodResolver(currencyEditSchema),
    defaultValues: { symbol: '', active: true },
  });
  const { reset, control, handleSubmit } = form;

  useEffect(() => {
    if (open && currency) {
      reset({ symbol: currency.symbol, active: currency.active });
    }
  }, [open, currency, reset]);

  const submitEdit: SubmitHandler<CurrencyEditValues> = (values) => {
    onSubmit({ symbol: values.symbol, active: values.active });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('dict.currencies.editTitle')}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(submitEdit)} noValidate>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {currency ? (
              <TextField label={t('dict.currencies.code')} value={currency.code} fullWidth disabled />
            ) : null}
            <Controller
              name="symbol"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('dict.currencies.symbol')}
                  required
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label={t('dict.status.active')}
                />
              )}
            />
          </Stack>
          <DialogActions>
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained" disabled={isPending}>
              {t('common.save')}
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
